/**
 * Round 93r (Feb 2026) — Ex-Div Alert pop-up modal.
 *
 * Fires once per calendar day (ET) when the user has at least one
 * upcoming ex-div event matching their enabled windows (5d / 3d / 1d).
 * Default state ships with portfolio_enabled=True at the 1-day window
 * only — quietest sensible default. Settings governing the windows
 * live on the Calendar page (portfolio + watchlist) and the watchlist
 * top-of-tab section (watchlist only).
 *
 * Display style: ONE modal listing every alert as a row. "Dismiss all"
 * suppresses the popup server-side until the next ET calendar day.
 * Per-row "x" dismisses just that row for the current session.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, BellRing } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format helpers
const fmt$ = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmt$4 = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  const rounded = Math.round(n * 10000) / 10000;
  return `$${rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
};
const fmtShares4 = (v) => {
  if (v == null) return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  if (Number.isInteger(n)) return n.toLocaleString();
  const rounded = Math.round(n * 10000) / 10000;
  return rounded.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

function urgencyTone(days) {
  if (days <= 1) return { badge: 'bg-rose-600 text-white', card: 'border-rose-500/50 bg-rose-50/40 dark:bg-rose-950/20', label: days === 0 ? 'EX-DIV TODAY' : 'EX-DIV TOMORROW' };
  if (days <= 3) return { badge: 'bg-amber-500 text-white', card: 'border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/20', label: `EX-DIV IN ${days} DAYS` };
  return { badge: 'bg-emerald-600 text-white', card: 'border-emerald-400/50 bg-emerald-50/40 dark:bg-emerald-950/20', label: `EX-DIV IN ${days} DAYS` };
}

export default function ExDivAlertModal({ portfolioId, onStockClick }) {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());

  const fetchAlerts = useCallback(async () => {
    try {
      const url = portfolioId
        ? `${API}/exdiv-alerts/upcoming?portfolio_id=${encodeURIComponent(portfolioId)}`
        : `${API}/exdiv-alerts/upcoming`;
      const { data } = await axios.get(url);
      const list = Array.isArray(data?.alerts) ? data.alerts : [];
      const dismissedToday = !!data?.dismissed_today;
      setAlerts(list);
      if (!dismissedToday && list.length > 0) {
        setOpen(true);
      }
    } catch {
      // Silent — alerts are non-essential. Don't bother the user with
      // an error toast for a passive feature.
    }
  }, [portfolioId]);

  // Trigger ONCE on mount after a 3-second delay (per user spec: "first
  // 3 second fetch from the portfolio page"). Refetches when portfolio
  // changes so a user switching portfolios still gets matching alerts.
  useEffect(() => {
    const t = setTimeout(fetchAlerts, 3000);
    return () => clearTimeout(t);
  }, [fetchAlerts]);

  const visibleAlerts = useMemo(
    () => alerts.filter((a) => !hiddenKeys.has(`${a.ticker}-${a.ex_div_date}`)),
    [alerts, hiddenKeys]
  );

  const handleDismissRow = (a) => {
    const k = `${a.ticker}-${a.ex_div_date}`;
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      next.add(k);
      return next;
    });
  };

  const handleDismissAll = async () => {
    try {
      await axios.post(`${API}/exdiv-alerts/dismiss`, { days: 1 });
    } catch {
      // Even if the persist call fails, close the modal locally.
    }
    setOpen(false);
  };

  const handleSnooze3 = async () => {
    try {
      const { data } = await axios.post(`${API}/exdiv-alerts/dismiss`, { days: 3 });
      if (data?.capped) {
        // Surface the cap to the user so they know we trimmed the
        // snooze to keep them from missing the soonest ex-div.
        const ev = data?.capped_by_event_ticker || '';
        const eff = data?.effective_days || 1;
        toast.message(
          `Snooze trimmed to ${eff} day${eff === 1 ? '' : 's'}` +
            (ev ? ` — ${ev} ex-div coming up.` : '.'),
          { duration: 5000 }
        );
      } else {
        toast.success('Snoozed for 3 days.');
      }
    } catch {
      // best-effort — close anyway
    }
    setOpen(false);
  };

  // Auto-close when every row was per-row dismissed: handled inline by
  // the `return null` guard below — no separate useEffect needed.

  if (!open || visibleAlerts.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleDismissAll}
      data-testid="exdiv-alert-modal-backdrop"
    >
      <div
        className="bg-card rounded-xl border-2 border-violet-500/30 shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="exdiv-alert-modal"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            <h2 className="text-base font-bold tracking-tight" data-testid="exdiv-alert-modal-title">
              Upcoming Ex-Dividend Dates
            </h2>
          </div>
          <button
            type="button"
            onClick={handleDismissAll}
            className="p-1 rounded hover:bg-muted transition-colors"
            title="Dismiss all (won't show again today)"
            data-testid="exdiv-alert-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Subhead */}
        <div className="px-4 py-2 text-xs text-muted-foreground font-semibold bg-muted/30 border-b border-border">
          You have {visibleAlerts.length} ex-dividend {visibleAlerts.length === 1 ? 'event' : 'events'} coming up. Buy
          before the ex-date to qualify for the next payment.
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto" data-testid="exdiv-alert-list">
          <ul className="divide-y divide-border">
            {visibleAlerts.map((a) => {
              const tone = urgencyTone(a.days_until_ex);
              const sharesLabel = fmtShares4(a.shares);
              const total = a.estimated_total;
              return (
                <li
                  key={`${a.ticker}-${a.ex_div_date}`}
                  className={`p-3 border-l-4 ${tone.card}`}
                  data-testid={`exdiv-alert-row-${a.ticker}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <button
                          type="button"
                          onClick={() => {
                            handleDismissAll();
                            onStockClick?.(a.ticker);
                          }}
                          className="font-mono text-sm font-bold text-violet-700 dark:text-violet-300 hover:underline"
                          title={`Open ${a.ticker} stock details`}
                          data-testid={`exdiv-alert-ticker-${a.ticker}`}
                        >
                          {a.ticker}
                        </button>
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${tone.badge}`}
                          data-testid={`exdiv-alert-urgency-${a.ticker}`}
                        >
                          {tone.label}
                        </span>
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            a.source === 'watchlist'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                          }`}
                        >
                          {a.source === 'watchlist' ? 'Watchlist' : 'Portfolio'}
                        </span>
                        {a.name && (
                          <span className="text-[11px] text-muted-foreground truncate">{a.name}</span>
                        )}
                      </div>
                      <div className="text-xs text-foreground space-y-0.5">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono">
                          <span>
                            <span className="text-muted-foreground">Ex-Div:</span>{' '}
                            <span className="font-semibold text-amber-700 dark:text-amber-300">
                              {fmtDate(a.ex_div_date)}
                            </span>
                          </span>
                          {a.pay_date && (
                            <span>
                              <span className="text-muted-foreground">Pay:</span>{' '}
                              <span className="font-semibold text-green-700 dark:text-green-400">
                                {fmtDate(a.pay_date)}
                              </span>
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px]">
                          {a.per_share != null && (
                            <span>
                              <span className="text-muted-foreground">/sh:</span>{' '}
                              <span className="font-semibold">{fmt$4(a.per_share)}</span>
                            </span>
                          )}
                          {sharesLabel && (
                            <span>
                              <span className="text-muted-foreground">× </span>
                              <span className="font-semibold">{sharesLabel}</span>
                              <span className="text-muted-foreground"> sh</span>
                            </span>
                          )}
                          {total != null && (
                            <span>
                              <span className="text-muted-foreground">=</span>{' '}
                              <span className="font-bold text-green-700 dark:text-green-400">{fmt$(total)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDismissRow(a)}
                      className="shrink-0 p-1 rounded hover:bg-muted transition-colors"
                      title="Hide this alert (this session)"
                      data-testid={`exdiv-alert-row-dismiss-${a.ticker}`}
                    >
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[10px] text-muted-foreground leading-relaxed flex-1 min-w-[160px]">
            Tip: change which windows (5d / 3d / 1d) trigger this popup from the Dividend Calendar page settings.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSnooze3}
              className="text-xs font-bold px-3 py-1.5 rounded-md bg-muted hover:bg-muted/70 text-foreground transition-colors"
              title="Suppress this popup for 3 days. We'll never snooze past the day before your soonest upcoming ex-div."
              data-testid="exdiv-alert-snooze-3d-btn"
            >
              Snooze 3 days
            </button>
            <button
              type="button"
              onClick={handleDismissAll}
              className="text-xs font-bold px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white transition-colors"
              data-testid="exdiv-alert-dismiss-all-btn"
            >
              Dismiss all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
